import { useState } from "react";
import type { Exercise, AttemptResult } from "../../types";
import { api } from "../../api/client";
import FeedbackPanel from "./FeedbackPanel";

interface Props {
  exercise: Exercise;
  onDone: (passed: boolean) => void;
  onSkip?: () => void;
}

export default function ClozeExercise({ exercise, onDone, onSkip }: Props) {
  const [answer, setAnswer] = useState("");
  const [result, setResult] = useState<AttemptResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!answer.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      setResult(await api.attemptExercise(exercise.id, answer));
    } catch (err) {
      console.error("Attempt failed:", err);
      setError("Couldn't check the answer. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const retry = () => {
    setResult(null);
    setAnswer("");
    setError(null);
  };

  // Render the cloze with the blank visually emphasized.
  const parts = exercise.text.split(/_{2,}/);

  return (
    <div>
      <p className="text-[1.05rem] text-ink leading-relaxed font-display">
        {parts[0]}
        <span className={`inline-block min-w-[3ch] border-b-2 mx-1 text-center ${result ? "border-success text-success" : "border-accent text-accent"}`}>
          {result ? exercise.answer : "…"}
        </span>
        {parts[1] ?? ""}
      </p>

      {!result && (
        <div className="flex gap-2 mt-5">
          <input
            className="input-base flex-1 focus:outline-none"
            value={answer}
            autoFocus
            placeholder="Type the missing word…"
            onChange={(e) => setAnswer(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          />
          <button className="btn-primary text-sm px-5" onClick={submit} disabled={submitting || !answer.trim()}>
            Check <span className="opacity-60 ml-1">↵</span>
          </button>
          {onSkip && <button onClick={onSkip} className="btn-quiet text-xs px-3 py-1.5">Skip</button>}
        </div>
      )}

      {error && <p className="text-error text-xs mt-3">{error}</p>}

      {result && (
        <FeedbackPanel correct={result.is_correct} onNext={() => onDone(result.is_correct)} onRetry={retry}>
          {!result.is_correct && (
            <>
              <div>
                Your answer: <span className="text-error font-medium">{answer}</span>
              </div>
              <div>
                Correct answer: <span className="text-success font-medium">{result.correct_answer}</span>
              </div>
            </>
          )}
          {result.explanation && <div className="italic">{result.explanation}</div>}
        </FeedbackPanel>
      )}
    </div>
  );
}
